/**
 * Demo Domain — Profile Registry
 *
 * Explicit registry pattern (no side-effect imports) so demo profiles
 * can be added incrementally for the official release without touching
 * the seeder, and so unused profiles remain tree-shakeable.
 *
 * Usage:
 *   import { registerAllDemoProfiles } from './demo'
 *   registerAllDemoProfiles()  // called once before loadDemoState()
 */

import type { DemoLearnerProfile } from './types'

const _profiles = new Map<string, DemoLearnerProfile>()
let _registrationComplete = false

/**
 * Register a single demo learner profile.
 * Last write wins — useful for tests that want to override a profile.
 */
export function registerDemoProfile(profile: DemoLearnerProfile): void {
  _profiles.set(profile.id, profile)
}

/** Return all currently-registered profiles in insertion order. */
export function getDemoProfiles(): DemoLearnerProfile[] {
  return Array.from(_profiles.values())
}

/** Look up a profile by id. */
export function getDemoProfile(id: string): DemoLearnerProfile | undefined {
  return _profiles.get(id)
}

/** Has the canonical demo set been registered yet? */
export function isDemoRegistryReady(): boolean {
  return _registrationComplete
}

/** Clear the registry — primarily for tests. */
export function _resetDemoRegistry(): void {
  _profiles.clear()
  _registrationComplete = false
}

/**
 * Register the canonical 8 demo learners that ship with Meester Djay.
 * Idempotent — safe to call multiple times.
 *
 * The actual learner data lives in `src/demo/seeding/learnerProfiles.ts`.
 * This registry tracks the *metadata* so future PRs can split each
 * profile into its own file without breaking call sites.
 */
export function registerAllDemoProfiles(): void {
  if (_registrationComplete) return

  registerDemoProfile({
    id: 'djayden',
    displayName: 'Djayden',
    grade: 7,
    archetype: 'power_user',
  })
  registerDemoProfile({
    id: 'emma',
    displayName: 'Emma',
    grade: 7,
    archetype: 'language_specialist',
  })
  registerDemoProfile({
    id: 'noah',
    displayName: 'Noah',
    grade: 7,
    archetype: 'balanced',
  })
  registerDemoProfile({
    id: 'sophie',
    displayName: 'Sophie',
    grade: 8,
    archetype: 'top_student',
  })
  registerDemoProfile({
    id: 'liam',
    displayName: 'Liam',
    grade: 8,
    archetype: 'struggling',
  })
  registerDemoProfile({
    id: 'noor',
    displayName: 'Noor',
    grade: 8,
    archetype: 'all_rounder',
  })
  registerDemoProfile({
    id: 'yara',
    displayName: 'Yara',
    grade: 6,
    archetype: 'math_focus',
  })
  registerDemoProfile({
    id: 'sem',
    displayName: 'Sem',
    grade: 6,
    archetype: 'topo_focus',
  })
  registerDemoProfile({
    id: 'demo',
    displayName: 'Demo Djay',
    grade: 8,
    archetype: 'full_access',
  })

  _registrationComplete = true
}
